import React from 'react';
import {Project} from '../../domain/types';
import ProjectRow, {ProjectRowActions} from './ProjectRow';
import {NewProjectButton} from './ProjectsToolbar';
import {SearchIcon, XIcon} from '../Icons';

interface ProjectsTableProps extends ProjectRowActions {
  projects: Project[];
  currentProjectId: string | null;
  isLoading: boolean;
  isBusy: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onCreate: () => void;
  /**
   * Why a given project cannot be deleted, or null when it can. Answered by the container rather
   * than derived here: the rule counts every project the user has, and `projects` is the search
   * filtered subset.
   */
  deleteBlockedReason: (project: Project) => string | null;
}

const HEADINGS = [
  {label: 'Project', span: 'md:col-span-4'},
  {label: 'Transcriptions', span: 'md:col-span-2'},
  {label: 'Default word list', span: 'md:col-span-2'},
  {label: 'Updated', span: 'md:col-span-2'},
  {label: '', span: 'md:col-span-2'},
];

/**
 * Every project, in the workspace's table shape: a titled panel, one search box, column headings,
 * and a row per project that owns its own actions.
 *
 * The desktop-era page used a three-across card grid, which spent a whole card on two numbers and
 * put the same four buttons in twelve different places on screen.
 */
const ProjectsTable: React.FC<ProjectsTableProps> = ({
  projects,
  currentProjectId,
  isLoading,
  isBusy,
  search,
  onSearchChange,
  onCreate,
  deleteBlockedReason,
  ...rowActions
}) => {
  const hasSearch = search.trim().length > 0;

  return (
    <section className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-800">
        <h2 className="text-sm font-semibold text-gray-100">
          All projects
          {projects.length > 0 && <span className="ml-2 font-normal text-gray-500">{projects.length}</span>}
        </h2>

        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
          <input
            type="search"
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            placeholder="Search projects…"
            aria-label="Search projects by name"
            className="w-56 rounded-lg border border-gray-800 bg-gray-950/60 py-1.5 pl-8 pr-7 text-xs
             text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
             focus:ring-1 focus:ring-indigo-500 [&::-webkit-search-cancel-button]:hidden"
          />
          {hasSearch && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-300 cursor-pointer"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </header>

      {projects.length > 0 && (
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 border-b border-gray-800
         bg-gray-950/40 text-[11px] font-medium uppercase tracking-wide text-gray-500">
          {HEADINGS.map(heading => (
            <div key={heading.label || 'actions'} className={`col-span-12 ${heading.span}`}>
              {heading.label}
            </div>
          ))}
        </div>
      )}

      {isLoading && projects.length === 0 && (
        <div className="px-4 py-10 text-center text-sm text-gray-500">Loading your projects…</div>
      )}

      {!isLoading && projects.length === 0 && !hasSearch && (
        <div className="flex flex-col items-center gap-1 px-4 py-12 text-center">
          <p className="text-sm font-medium text-gray-200">No projects yet</p>
          <p className="max-w-md text-sm text-gray-500">
            Create one to keep a show&apos;s audio, transcriptions and word list together.
          </p>
          <div className="mt-3">
            <NewProjectButton onClick={onCreate} />
          </div>
        </div>
      )}

      {!isLoading && projects.length === 0 && hasSearch && (
        <div className="px-4 py-12 text-center">
          <p className="text-sm text-gray-400">No projects match “{search.trim()}”.</p>
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer"
          >
            Clear search
          </button>
        </div>
      )}

      {projects.map(project => (
        <ProjectRow
          key={project.id}
          project={project}
          isCurrent={project.id === currentProjectId}
          isBusy={isBusy}
          deleteBlockedReason={deleteBlockedReason(project)}
          {...rowActions}
        />
      ))}
    </section>
  );
};

export default ProjectsTable;
